import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSave } from '@fortawesome/free-solid-svg-icons';
import { motion } from 'framer-motion';
import { WorkflowConfig } from '../../types/workflow';

interface WorkflowConfigModalProps {
  config: WorkflowConfig;
  onClose: () => void;
  onSave: (config: Partial<WorkflowConfig>) => void;
  isProcessing: boolean;
}

const WorkflowConfigModal: React.FC<WorkflowConfigModalProps> = ({
  config,
  onClose,
  onSave,
  isProcessing
}) => {
  const [formData, setFormData] = useState(config);

  const handleSave = () => {
    onSave(formData);
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Workflow Konfiguration</h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        
        <div className="p-6 space-y-6">
          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.enabled}
                onChange={(e) => handleInputChange('enabled', e.target.checked)}
                className="mr-2"
              />
              <span className="font-medium">Workflows aktiviert</span>
            </label>
          </div>
          
          <div>
            <h3 className="font-semibold mb-3">Erinnerungseinstellungen (Tage)</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Erste Erinnerung
                </label>
                <input
                  type="number"
                  value={formData.reminders.initial}
                  onChange={(e) => handleInputChange('reminders', {
                    ...formData.reminders,
                    initial: parseInt(e.target.value)
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Standard Erinnerung
                </label>
                <input
                  type="number"
                  value={formData.reminders.reminder}
                  onChange={(e) => handleInputChange('reminders', {
                    ...formData.reminders,
                    reminder: parseInt(e.target.value)
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex justify-end space-x-3 p-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
          >
            Abbrechen
          </button>
          <button
            onClick={handleSave}
            disabled={isProcessing}
            className="px-4 py-2 bg-purple-500 text-white rounded-md hover:bg-purple-600 disabled:opacity-50"
          >
            <FontAwesomeIcon icon={faSave} className="mr-2" />
            {isProcessing ? 'Speichert...' : 'Speichern'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default WorkflowConfigModal;